import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SERVICES as STATIC_SERVICES } from "@/data/services";
import { INDUSTRIES as STATIC_INDUSTRIES } from "@/data/industries";
import { ENGAGEMENT_MODELS as STATIC_ENGAGEMENT } from "@/data/engagement";

/**
 * Fetch services from API with static fallback so the UI never shows a blank state.
 * Any admin edits made via the CMS override the static data on next load.
 */
export function useServices() {
    const [items, setItems] = useState(STATIC_SERVICES);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        let mounted = true;
        setLoading(true);
        api.get("/services")
            .then(({ data }) => { if (mounted && data.items?.length) setItems(data.items); })
            .catch(() => {})
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, []);
    return { items, loading };
}

export function useIndustries() {
    const [items, setItems] = useState(STATIC_INDUSTRIES);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        let mounted = true;
        setLoading(true);
        api.get("/industries")
            .then(({ data }) => { if (mounted && data.items?.length) setItems(data.items); })
            .catch(() => {})
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, []);
    return { items, loading };
}

export function useEngagementModels() {
    const [items, setItems] = useState(STATIC_ENGAGEMENT);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        let mounted = true;
        setLoading(true);
        api.get("/engagement-models")
            .then(({ data }) => { if (mounted && data.items?.length) setItems(data.items); })
            .catch(() => {})
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, []);
    return { items, loading };
}

export function useServiceBySlug(slug) {
    const [item, setItem] = useState(STATIC_SERVICES.find((s) => s.slug === slug) || null);
    const [notFound, setNotFound] = useState(false);
    useEffect(() => {
        let mounted = true;
        setNotFound(false);
        api.get(`/services/${slug}`)
            .then(({ data }) => { if (mounted) setItem(data); })
            .catch((e) => {
                if (mounted && !STATIC_SERVICES.find((s) => s.slug === slug)) setNotFound(true);
            });
        return () => { mounted = false; };
    }, [slug]);
    return { item, notFound };
}

export function useIndustryBySlug(slug) {
    const [item, setItem] = useState(STATIC_INDUSTRIES.find((i) => i.slug === slug) || null);
    const [notFound, setNotFound] = useState(false);
    useEffect(() => {
        let mounted = true;
        setNotFound(false);
        api.get(`/industries/${slug}`)
            .then(({ data }) => { if (mounted) setItem(data); })
            .catch(() => {
                if (mounted && !STATIC_INDUSTRIES.find((i) => i.slug === slug)) setNotFound(true);
            });
        return () => { mounted = false; };
    }, [slug]);
    return { item, notFound };
}

export function useProducts() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        let mounted = true;
        setLoading(true);
        api.get("/products")
            .then(({ data }) => { if (mounted && data.items?.length) setItems(data.items); })
            .catch(() => {})
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, []);
    return { items, loading };
}

/**
 * Fetch page-level banner/CTA content from API.
 * Returns null while loading — consumers should fall back to hardcoded defaults.
 */
export function usePageContent(pageId) {
    const [data, setData] = useState(null);
    useEffect(() => {
        if (!pageId) return;
        let mounted = true;
        api.get(`/page-content/${pageId}`)
            .then(({ data }) => { if (mounted && data && Object.keys(data).length > 0) setData(data); })
            .catch(() => {});
        return () => { mounted = false; };
    }, [pageId]);
    return data;
}

export function useSettings(key) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let mounted = true;
        setLoading(true);
        api.get(`/settings/${key}`)
            .then(({ data }) => { if (mounted) setData(data); })
            .catch(() => {})
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, [key]);
    return { data, loading };
}
